/**
 * Provides landing-page attribution, accordion, carousel, and CTA behavior.
 */
(() => {
  const campaignParameters = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'fbclid',
  ];

  /**
   * Returns a URL with campaign parameters copied from the current page.
   *
   * @param {string} destination - CTA destination.
   * @returns {string} Attributed destination.
   */
  const getAttributedUrl = (destination) => {
    try {
      const url = new URL(destination, window.location.origin);
      const currentParameters = new URLSearchParams(window.location.search);

      campaignParameters.forEach((parameter) => {
        if (currentParameters.has(parameter)) {
          url.searchParams.set(parameter, currentParameters.get(parameter));
        }
      });

      return url.toString();
    } catch (error) {
      return destination;
    }
  };

  /**
   * Configures campaign attribution and analytics for CTA links.
   *
   * @param {ParentNode} scope - Section or document to initialize.
   */
  const initializeCtas = (scope) => {
    window.dataLayer = window.dataLayer || [];

    scope.querySelectorAll('[data-skincare-cta]').forEach((link) => {
      link.href = getAttributedUrl(link.href);

      link.addEventListener('click', () => {
        const destination = new URL(link.href);

        window.dataLayer.push({
          event: 'advertorial_cta_click',
          cta_text: link.textContent.trim(),
          destination: destination.pathname,
        });
      });
    });
  };

  /**
   * Tracks article scroll depth once per page view.
   */
  const initializeScrollTracking = () => {
    if (document.documentElement.dataset.skincareScrollTracking === 'true') {
      return;
    }

    document.documentElement.dataset.skincareScrollTracking = 'true';
    const sentMilestones = new Set();

    window.addEventListener(
      'scroll',
      () => {
        const scrollableHeight =
          document.documentElement.scrollHeight - window.innerHeight;

        if (scrollableHeight <= 0) {
          return;
        }

        const percentage = Math.round(
          (window.scrollY / scrollableHeight) * 100,
        );

        [25, 50, 75, 90].forEach((milestone) => {
          if (
            percentage >= milestone &&
            !sentMilestones.has(milestone)
          ) {
            sentMilestones.add(milestone);
            window.dataLayer.push({
              event: 'advertorial_scroll',
              percent: milestone,
            });
          }
        });
      },
      { passive: true },
    );
  };

  /**
   * Tracks the first product-reveal impression.
   *
   * @param {HTMLElement} section - Product reveal section.
   */
  const initializeProductRevealTracking = (section) => {
    if (!('IntersectionObserver' in window)) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          window.dataLayer.push({
            event: 'advertorial_product_reveal',
          });
          observer.disconnect();
        });
      },
      { threshold: 0.25 },
    );

    observer.observe(section);
  };

  /**
   * Moves the reading time under the author name on small screens, where the
   * byline collapses to a two-column grid reserved for the sponsor badge.
   *
   * @param {HTMLElement} section - Editorial hero section.
   */
  const initializeByline = (section) => {
    const authorDetails = section.querySelector('[data-author-details]');
    const metadata = section.querySelector('[data-author-metadata]');
    const readingTime = section.querySelector('[data-reading-time]');

    if (!authorDetails || !metadata || !readingTime) {
      return;
    }

    const desktopPosition = document.createComment(
      'reading-time-desktop-position',
    );
    const mediaQuery = window.matchMedia('(max-width: 768px)');

    metadata.insertBefore(desktopPosition, readingTime);

    const synchronizeByline = () => {
      if (mediaQuery.matches) {
        authorDetails.append(readingTime);
        return;
      }

      desktopPosition.after(readingTime);
    };

    mediaQuery.addEventListener('change', synchronizeByline);
    synchronizeByline();
  };

  /**
   * Configures a routine accordion and synchronized media.
   *
   * @param {HTMLElement} accordion - Accordion root.
   */
  const initializeAccordion = (accordion) => {
    const triggers = [...accordion.querySelectorAll('[data-accordion-trigger]')];
    const panels = [...accordion.querySelectorAll('[data-accordion-panel]')];
    const images = [...accordion.querySelectorAll('[data-accordion-image]')];

    triggers.forEach((trigger, activeIndex) => {
      trigger.addEventListener('click', () => {
        triggers.forEach((currentTrigger, index) => {
          const isActive = index === activeIndex;
          const icon = currentTrigger.querySelector('[data-accordion-icon]');

          currentTrigger.setAttribute('aria-expanded', String(isActive));
          currentTrigger
            .closest('[data-accordion-item]')
            .classList.toggle('is-open', isActive);
          panels[index].hidden = !isActive;
          images[index]?.classList.toggle('is-active', isActive);

          if (icon) {
            icon.textContent = isActive ? '−' : '+';
          }
        });
      });
    });
  };

  /**
   * Configures one customer-video carousel.
   *
   * @param {HTMLElement} carousel - Carousel root.
   */
  const initializeCarousel = (carousel) => {
    const track = carousel.querySelector('[data-carousel-track]');
    const cards = [...track.children];
    const previousButton = carousel.querySelector('[data-carousel-previous]');
    const nextButton = carousel.querySelector('[data-carousel-next]');
    const position = carousel.querySelector('[data-carousel-position]');
    const positionTemplate = position.dataset.positionTemplate;

    if (!cards.length) {
      return;
    }

    const getStep = () => cards[0].getBoundingClientRect().width + 16;
    const getIndex = () => Math.round(track.scrollLeft / getStep());

    const synchronizeControls = () => {
      const currentIndex = getIndex();

      previousButton.disabled = currentIndex === 0;
      nextButton.disabled = currentIndex >= cards.length - 1;
      position.textContent = positionTemplate
        .replace('[current]', String(currentIndex + 1))
        .replace('[total]', String(cards.length));
    };

    const move = (direction) => {
      const nextIndex = Math.max(
        0,
        Math.min(cards.length - 1, getIndex() + direction),
      );
      const behavior = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches
        ? 'instant'
        : 'smooth';

      track.scrollTo({
        left: nextIndex * getStep(),
        behavior,
      });
    };

    previousButton.addEventListener('click', () => move(-1));
    nextButton.addEventListener('click', () => move(1));
    track.addEventListener('scroll', synchronizeControls, { passive: true });
    track.addEventListener('keydown', (event) => {
      if (event.target !== track) {
        return;
      }

      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        move(event.key === 'ArrowRight' ? 1 : -1);
      }
    });

    carousel.querySelectorAll('[data-video-frame]').forEach((frame) => {
      const video = frame.querySelector('video');
      const button = frame.querySelector('[data-video-control]');

      if (!video || !button) {
        return;
      }

      const synchronizeVideoControl = () => {
        const isPlaying = !video.paused && !video.ended;

        button.classList.toggle('is-playing', isPlaying);
        button.setAttribute(
          'aria-label',
          isPlaying ? button.dataset.pauseLabel : button.dataset.playLabel,
        );
      };

      const primeVideo = () => {
        if (video.dataset.primed === 'true' || !video.paused) {
          return;
        }

        video.dataset.primed = 'true';
        video.preload = 'auto';
        video.load();
      };

      button.addEventListener('click', () => {
        if (video.paused || video.ended) {
          carousel.querySelectorAll('video').forEach((otherVideo) => {
            if (otherVideo !== video) {
              otherVideo.pause();
            }
          });
          video.play().catch(synchronizeVideoControl);
          return;
        }

        video.pause();
      });

      video.addEventListener('playing', () => {
        frame.classList.add('is-started');
      });

      ['play', 'pause', 'ended'].forEach((eventName) => {
        video.addEventListener(eventName, synchronizeVideoControl);
      });
      synchronizeVideoControl();

      if ('IntersectionObserver' in window) {
        const preloadObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            primeVideo();
            preloadObserver.disconnect();
          });
        }, { rootMargin: '400px 0px' });

        preloadObserver.observe(frame);
      }
    });

    window.addEventListener('resize', synchronizeControls, { passive: true });
    synchronizeControls();
  };

  /**
   * Configures the post-reveal mobile CTA.
   *
   * @param {HTMLElement} section - Final CTA section.
   */
  const initializeMobileCta = (section) => {
    const mobileCta = section.querySelector('[data-mobile-cta]');
    const productReveal = document.querySelector('[data-product-reveal]');
    const finalCta = section.querySelector('[data-final-cta]');

    if (!mobileCta || !productReveal) {
      return;
    }

    /*
      .shopify-section is position: relative, so offsetTop is measured from
      that wrapper. getBoundingClientRect is the document position.
    */
    const documentTop = (element) =>
      element.getBoundingClientRect().top + window.scrollY;

    const synchronizeMobileCta = () => {
      const revealEnd =
        documentTop(productReveal) + productReveal.offsetHeight;
      const afterReveal = window.scrollY > revealEnd;
      const finalCtaTop = finalCta ? documentTop(finalCta) : null;
      const beforeFinalCta =
        finalCtaTop === null ||
        window.scrollY < finalCtaTop - window.innerHeight * 0.4;

      mobileCta.classList.toggle(
        'is-visible',
        afterReveal && beforeFinalCta,
      );
    };

    window.addEventListener('scroll', synchronizeMobileCta, {
      passive: true,
    });
    synchronizeMobileCta();
  };

  /**
   * Initializes a landing-page section once.
   *
   * @param {HTMLElement} section - Section root.
   */
  const initializeSection = (section) => {
    if (section.dataset.skincareInitialized === 'true') {
      return;
    }

    section.dataset.skincareInitialized = 'true';
    initializeCtas(section);
    initializeScrollTracking();

    section.querySelectorAll('[data-routine-accordion]').forEach(
      initializeAccordion,
    );
    section.querySelectorAll('[data-customer-carousel]').forEach(
      initializeCarousel,
    );

    if (section.matches('[data-final-cta-section]')) {
      initializeMobileCta(section);
    }

    if (section.matches('[data-product-reveal]')) {
      initializeProductRevealTracking(section);
    }

    if (section.matches('[data-editorial-hero]')) {
      initializeByline(section);
    }
  };

  document.querySelectorAll('[data-skincare-section]').forEach(
    initializeSection,
  );

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.matches('[data-skincare-section]')) {
      initializeSection(event.target);
    }

    event.target.querySelectorAll('[data-skincare-section]').forEach(
      initializeSection,
    );
  });
})();
